import React from "react";
import { ArrowLeft, Pencil } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";

import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import Link from "@/components/link";
import { Button } from "@/components/ui/button";
import { Paragraph } from "@/components/ui/typography";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import useTimer from "@/hooks/use-timer";

import { cn } from "@/lib/utils";

export const EMAIL_ADDRESS_REGEX =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

function AuthSceneRegister({ onResendOtp, onBack }) {
  const {
    control,
    formState: { isSubmitting },
  } = useFormContext();

  const phoneNumber = useWatch({ name: "phone" });

  const {
    seconds,
    isActive: isActiveResendTimer,
    reset: resetResendTimer,
  } = useTimer(30, { autoStart: true });

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-x-2 -mx-7">
        <Button
          variant="link"
          size="icon"
          className="size-6"
          type="button"
          onClick={onBack}
        >
          <ArrowLeft size={18} />
        </Button>
        <span>OTP Verification</span>
      </div>

      <div className="flex flex-col my-4 flex-1">
        <div className="flex gap-x-2">
          <span className="text-grayscale-label">
            OTP sent to +91-{phoneNumber}
          </span>
          <button
            type="button"
            className="flex items-center text-primary gap-x-1"
            onClick={onBack.bind(this, "EDIT_PHONE")}
          >
            <Pencil size={20} fill="currentColor" stroke="#fff" />
            <span>Edit</span>
          </button>
        </div>

        <div className="space-y-6">
          <div className="flex flex-col mt-3.75 gap-y-3.75">
            <label
              htmlFor="phoneOtp"
              className="uppercase text-grayscale-label text-xs"
            >
              Enter OTP
            </label>

            <FormField
              control={control}
              name="phoneOtp"
              rules={{ required: true, pattern: /^\d{6}$/ }}
              render={({ field }) => (
                <FormItem>
                  <InputOTP id="phoneOtp" maxLength={6} {...field}>
                    <InputOTPGroup className="w-full justify-between [&>div]:!rounded [&>div]:!border [&>div]:shadow-none">
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>

                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end">
              <Button
                type="button"
                variant="link"
                size="small"
                className={cn(
                  "text-sm px-12 border-b border-dashed border-grayscale-line rounded-none !no-underline"
                )}
                disabled={isActiveResendTimer}
                onClick={() => {
                  onResendOtp();
                  resetResendTimer(true);
                }}
              >
                {isActiveResendTimer
                  ? `Resend OTP in ${seconds}s`
                  : "Resend OTP"}
              </Button>
            </div>
          </div>

          <FormField
            name="name"
            rules={{
              required: true,
              minLength: 3,
              maxLength: 50,
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Your Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="email"
            rules={{ required: true, pattern: EMAIL_ADDRESS_REGEX }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>E-Mail</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your email address" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-between items-center mt-6">
          <Button
            type="submit"
            size="small"
            className="text-white text-sm px-12"
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            Verify
          </Button>
        </div>

        <div className="flex flex-col items-center mt-auto">
          <Paragraph variant="b5" weight="light" className="space-x-1">
            <span>Trouble in Login or Sign Up?</span>
            <Button
              variant="link"
              size="small"
              className="!gap-x-1 font-normal hover:no-underline"
              asChild
            >
              <Link href="mailto:support@studyiq.com">Click Here</Link>
            </Button>
          </Paragraph>
        </div>
      </div>
    </div>
  );
}

export default AuthSceneRegister;
